// Areas tab → Review: how the areas performed over a week or a month
// (Oct 23, 2026). Greg: "at the end of each week, and month a review of areas."
//
// Every area active at any point in the window — the ones expired since too
// (canvas_area_review) — with the knocks inside its polygon by day and by
// person. Day by day answers "who worked which area and what came of it";
// by area ranks the areas; by person shows who knocked where, flagging anyone
// who knocked an area they were never put on. Monday's 1-on-1 links here.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchAreaReview, fetchDayStops, firstName, fmtSince, areaMapHref } from "../../../lib/canvasAreas";
import { useTabParam } from "../../../lib/useTabParam";
import { dayLabel, todayET } from "../../../lib/dailyCommitments";
import {
  monthRange, rangeLabel, reviewAreaName, reviewCsv, reviewDayRows, reviewPersonRows, reviewTotals,
  shiftRange, weekRange, workdaysIn, type DayStop, type ReviewArea,
} from "../../../lib/dailyAreas";
import DayAreasMap, { reviewMapAreas } from "./DayAreasMap";

const ROLE_SHORT: Record<string, string> = { asm: "ASM", team_lead: "TL", restore_manager: "RM", canvasser: "CNV", roof_specialist: "RS" };

function pct(num: number, den: number): string {
  return den > 0 ? `${Math.round((num / den) * 100)}%` : "—";
}

export default function AreaReview() {
  const today = todayET();
  const [span, setSpan] = useTabParam<"week" | "month">("span", "week", ["week", "month"]);
  const [anchor, setAnchor] = useState<string>(() => {
    const q = new URLSearchParams(window.location.search).get("on");
    return q && /^\d{4}-\d{2}-\d{2}$/.test(q) ? q : today;
  });
  const range = useMemo(() => (span === "week" ? weekRange(anchor) : monthRange(anchor)), [span, anchor]);
  const current = useMemo(() => (span === "week" ? weekRange(today) : monthRange(today)), [span, today]);
  const isCurrent = range.from === current.from;

  const [areas, setAreas] = useState<ReviewArea[] | null>(null);
  const [stops, setStops] = useState<DayStop[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setAreas(null); setErr(null);
    (async () => {
      try {
        // Stops are pinned on the week map only; a month would be clutter, so don't fetch them.
        const [a, s] = await Promise.all([
          fetchAreaReview(range.from, range.to),
          span === "week" ? fetchDayStops(range.from, range.to).catch(() => [] as DayStop[]) : Promise.resolve([] as DayStop[]),
        ]);
        if (cancelled) return;
        setAreas(a); setStops(s);
      } catch (e) {
        if (!cancelled) setErr((e as Error).message);
      }
    })();
    return () => { cancelled = true; };
  }, [range.from, range.to, span, tick]);

  const list = areas ?? [];
  const totals = useMemo(() => reviewTotals(list), [list]);
  const dayRows = useMemo(() => reviewDayRows(list, range.from, range.to), [list, range.from, range.to]);
  const personRows = useMemo(() => reviewPersonRows(list), [list]);
  const mapAreas = useMemo(() => reviewMapAreas(list, range.from, range.to), [list, range.from, range.to]);
  const workdays = workdaysIn(range.from, range.to);

  function exportCsv() {
    const blob = new Blob([reviewCsv(list, range.from, range.to)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `canvas-areas-${range.from}-to-${range.to}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  const seg = (on: boolean) => `rounded-md px-3 py-1 text-xs font-bold ${on ? "bg-orange-500 text-white" : "text-slate-600 hover:bg-slate-100"}`;

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white p-3">
        <div className="inline-flex rounded-lg bg-slate-100 p-0.5">
          <button type="button" onClick={() => setSpan("week")} className={seg(span === "week")}>Week</button>
          <button type="button" onClick={() => setSpan("month")} className={seg(span === "month")}>Month</button>
        </div>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => setAnchor(shiftRange(range, span, -1).from)} aria-label={`Previous ${span}`}
            className="rounded-md border border-slate-200 px-2 py-1 text-sm font-bold text-slate-600 hover:bg-slate-50">‹</button>
          <div className="min-w-[180px] text-center text-sm font-bold text-slate-900">{rangeLabel(range, span)}</div>
          <button type="button" onClick={() => setAnchor(shiftRange(range, span, 1).from)} aria-label={`Next ${span}`}
            className="rounded-md border border-slate-200 px-2 py-1 text-sm font-bold text-slate-600 hover:bg-slate-50">›</button>
          {!isCurrent && (
            <button type="button" onClick={() => setAnchor(today)} className="rounded-md px-2 py-1 text-xs font-semibold text-orange-600 hover:bg-orange-50">
              This {span}
            </button>
          )}
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <button type="button" onClick={exportCsv} disabled={!areas || list.length === 0}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
            Export CSV
          </button>
          <Link to="/canvas/manager-audit?mode=oneone" className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-bold text-white hover:opacity-90">
            Use in the 1-on-1 →
          </Link>
        </div>
      </div>

      {err && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
          {err} <button className="ml-2 font-semibold underline" onClick={() => setTick((n) => n + 1)}>Retry</button>
        </div>
      )}

      {/* Tiles */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Tile label="Areas worked" value={areas ? String(totals.worked) : "…"} hint={`of ${totals.areas} active in the ${span}`} />
        <Tile label="Area-days" value={areas ? String(totals.areaDays) : "…"} hint={`days an area was knocked · ${workdays} workdays in the ${span}`} />
        <Tile label="Knocks in areas" value={areas ? totals.knocks.toLocaleString() : "…"} hint="inside an area's outline, whoever knocked" tone="text-slate-900" />
        <Tile label="Talked" value={areas ? String(totals.talked) : "…"} hint={totals.talkRate == null ? "no knocks yet" : `${totals.talkRate}% of knocks`} tone="text-purple-700" />
        <Tile label="Appointments" value={areas ? String(totals.appts) : "…"} hint="set from area knocks" tone="text-cyan-700" />
        <Tile label="People who knocked" value={areas ? String(totals.knockers) : "…"} hint={`of ${totals.assigned} assigned to an area`} />
      </div>

      <div className="grid gap-4 lg:grid-cols-12">
        {/* Day by day */}
        <div className="rounded-xl border border-slate-200 bg-white lg:col-span-7">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-3 py-2">
            <div className="text-sm font-bold text-slate-900">Day by day</div>
            <div className="text-[11px] text-slate-500">Who worked which area, and what came of it</div>
          </div>
          {!areas && !err ? (
            <div className="p-6 text-center text-sm text-slate-400">Loading…</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-xs">
                <thead className="text-[10px] uppercase tracking-wide text-slate-500">
                  <tr className="border-b border-slate-200">
                    <th className="px-3 py-1.5 text-left font-semibold">Day</th>
                    <th className="px-2 py-1.5 text-left font-semibold">Area</th>
                    <th className="px-2 py-1.5 text-left font-semibold">Who knocked</th>
                    <th className="px-2 py-1.5 text-right font-semibold">Pins</th>
                    <th className="px-2 py-1.5 text-right font-semibold">Knocks</th>
                    <th className="px-2 py-1.5 text-right font-semibold">Talked</th>
                    <th className="px-2 py-1.5 text-right font-semibold">Appts</th>
                    <th className="px-2 py-1.5" />
                  </tr>
                </thead>
                <tbody className="tabular-nums">
                  {dayRows.map((row) => {
                    const label = dayLabel(row.day, { weekday: "short", day: "numeric" });
                    const dow = new Date(row.day + "T12:00:00Z").getUTCDay();
                    const weekend = dow === 0 || dow === 6;
                    if (row.areas.length === 0) {
                      if (span === "month" && weekend) return null;
                      return (
                        <tr key={row.day} className="border-b border-slate-100 text-slate-400">
                          <td className="px-3 py-1.5">{label}</td>
                          <td className="px-2 py-1.5" colSpan={6}>{row.day > today ? "" : "No area worked"}</td>
                          <td />
                        </tr>
                      );
                    }
                    return row.areas.map((r, i) => {
                      // Who knocked THIS day (canvas_area_review by_day[].people); never the window total.
                      const knockers = r.people;
                      return (
                        <tr key={`${row.day}-${r.area.area_group_id}`} className="border-b border-slate-100">
                          <td className="px-3 py-1.5 font-bold text-slate-800">{i === 0 ? label : ""}</td>
                          <td className="px-2 py-1.5">
                            <div className="font-semibold text-slate-900">{reviewAreaName(r.area)}</div>
                            <div className="text-[10px] text-slate-500">
                              {r.area.for_date ? `daily · ${dayLabel(r.area.for_date, { weekday: "short", month: "short", day: "numeric" })}` : "standing"}
                              {r.area.reps.length ? ` · ${r.area.reps.map((x) => firstName(x.full_name)).join(", ")}` : ""}
                              {r.area.status === "removed" ? " · expired" : ""}
                            </div>
                          </td>
                          <td className="px-2 py-1.5 text-slate-700">
                            {knockers.length === 0 && <span className="text-slate-400">—</span>}
                            {knockers.map((p) => (
                              <span key={p.user_id} className="mr-1.5 inline-flex items-center gap-1" title={`${p.knocks} knocks · ${p.talked} talked`}>
                                {firstName(p.full_name)}{knockers.length > 1 && <span className="text-[10px] text-slate-400">{p.knocks}</span>}
                                {!p.assigned && <span className="rounded bg-amber-50 px-1 text-[9px] font-bold text-amber-700" title="Knocked this area without being on it">not on it</span>}
                              </span>
                            ))}
                          </td>
                          <td className="px-2 py-1.5 text-right">{r.area.pin_count}</td>
                          <td className="px-2 py-1.5 text-right font-bold">{r.knocks}</td>
                          <td className="px-2 py-1.5 text-right text-purple-700">{r.talked}</td>
                          <td className="px-2 py-1.5 text-right text-cyan-700">{r.appts}</td>
                          <td className="px-2 py-1.5 text-right">
                            {r.area.status === "active" && (
                              <a href={areaMapHref(r.area.area_group_id)} target="_blank" rel="noopener" className="text-[11px] font-bold text-orange-700">Map ↗</a>
                            )}
                          </td>
                        </tr>
                      );
                    });
                  })}
                  <tr className="font-bold text-slate-900">
                    <td className="px-3 py-2">{span === "week" ? "Week" : "Month"}</td>
                    <td className="px-2 py-2">{totals.worked} {totals.worked === 1 ? "area" : "areas"}</td>
                    <td className="px-2 py-2">{totals.knockers} {totals.knockers === 1 ? "person" : "people"}</td>
                    <td />
                    <td className="px-2 py-2 text-right">{totals.knocks}</td>
                    <td className="px-2 py-2 text-right text-purple-700">{totals.talked}</td>
                    <td className="px-2 py-2 text-right text-cyan-700">{totals.appts}</td>
                    <td />
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Map */}
        <div className="lg:col-span-5">
          <DayAreasMap
            areas={mapAreas}
            stops={span === "week" ? stops : []}
            title={`Where the ${span} happened`}
            subtitle={span === "week" ? "Daily areas colored by weekday · standing areas dashed · the week's stops pinned" : "Daily areas colored by weekday · standing areas dashed"}
            height="h-72"
            empty={areas ? "No areas were active in this window." : "Loading…"}
            right={<Link to="/canvas?areas=on" className="text-[11px] font-bold text-orange-700">Open on canvas ↗</Link>}
          />
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* By area */}
        <div className="rounded-xl border border-slate-200 bg-white">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-3 py-2">
            <div className="text-sm font-bold text-slate-900">By area</div>
            <div className="text-[11px] text-slate-500">Busiest first</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px] text-xs">
              <thead className="text-[10px] uppercase tracking-wide text-slate-500">
                <tr className="border-b border-slate-200">
                  <th className="px-3 py-1.5 text-left font-semibold">Area</th>
                  <th className="px-2 py-1.5 text-right font-semibold">Pins</th>
                  <th className="px-2 py-1.5 text-right font-semibold">Knocks</th>
                  <th className="px-2 py-1.5 text-right font-semibold">Talk rate</th>
                  <th className="px-2 py-1.5 text-right font-semibold">Appts</th>
                  <th className="px-2 py-1.5 text-right font-semibold">Days</th>
                  <th className="px-2 py-1.5 text-right font-semibold">Deals</th>
                </tr>
              </thead>
              <tbody className="tabular-nums">
                {list.length === 0 && (
                  <tr><td className="px-3 py-4 text-center text-slate-400" colSpan={7}>{areas ? "No areas in this window." : "Loading…"}</td></tr>
                )}
                {list.map((a) => (
                  <tr key={a.area_group_id} className={`border-b border-slate-100 ${a.totals.knocks === 0 ? "text-slate-400" : ""}`}>
                    <td className="px-3 py-1.5">
                      <div className={`font-semibold ${a.totals.knocks === 0 ? "" : "text-slate-900"}`}>{reviewAreaName(a)}</div>
                      <div className="text-[10px] text-slate-500">
                        {a.for_date ? `daily · ${dayLabel(a.for_date, { weekday: "short", month: "short", day: "numeric" })}` : `standing since ${fmtSince(a.first_assigned_at)}`}
                        {a.status === "removed" ? " · expired" : ""}
                        {a.reps.length ? ` · ${a.reps.map((x) => firstName(x.full_name)).join(", ")}` : ""}
                      </div>
                    </td>
                    <td className="px-2 py-1.5 text-right">{a.pin_count}</td>
                    <td className="px-2 py-1.5 text-right font-bold">{a.totals.knocks}</td>
                    <td className="px-2 py-1.5 text-right">{pct(a.totals.talked, a.totals.knocks)}</td>
                    <td className="px-2 py-1.5 text-right text-cyan-700">{a.totals.appts}</td>
                    <td className="px-2 py-1.5 text-right">{a.totals.days}</td>
                    <td className="px-2 py-1.5 text-right" title={`${a.deals.claims} at claim or later · ${a.deals.builds} built`}>{a.deals.deals}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* By person */}
        <div className="rounded-xl border border-slate-200 bg-white">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-3 py-2">
            <div className="text-sm font-bold text-slate-900">By person</div>
            <div className="text-[11px] text-slate-500">Knocks inside any area</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px] text-xs">
              <thead className="text-[10px] uppercase tracking-wide text-slate-500">
                <tr className="border-b border-slate-200">
                  <th className="px-3 py-1.5 text-left font-semibold">Person</th>
                  <th className="px-2 py-1.5 text-left font-semibold">Areas knocked</th>
                  <th className="px-2 py-1.5 text-right font-semibold">Knocks</th>
                  <th className="px-2 py-1.5 text-right font-semibold">Talked</th>
                  <th className="px-2 py-1.5 text-right font-semibold">Appts</th>
                  <th className="px-2 py-1.5 text-right font-semibold">Days</th>
                </tr>
              </thead>
              <tbody className="tabular-nums">
                {personRows.length === 0 && (
                  <tr><td className="px-3 py-4 text-center text-slate-400" colSpan={6}>{areas ? "Nobody knocked an area in this window." : "Loading…"}</td></tr>
                )}
                {personRows.map((p) => (
                  <tr key={p.user_id} className={`border-b border-slate-100 ${p.knocks === 0 ? "text-slate-400" : ""}`}>
                    <td className="px-3 py-1.5 font-semibold">
                      {p.full_name} <span className="text-[9px] font-bold text-slate-400">{ROLE_SHORT[p.role ?? ""] ?? ""}</span>
                    </td>
                    <td className="px-2 py-1.5">
                      {p.areas.filter((x) => x.knocks > 0 || p.knocks === 0).map((x) => (
                        <span key={x.area.area_group_id} className="mr-1.5 inline-flex items-center gap-1">
                          {reviewAreaName(x.area)}
                          {!x.assigned && <span className="rounded bg-amber-50 px-1 text-[9px] font-bold text-amber-700">not assigned</span>}
                        </span>
                      ))}
                    </td>
                    <td className="px-2 py-1.5 text-right font-bold">{p.knocks}</td>
                    <td className="px-2 py-1.5 text-right text-purple-700">{p.talked}</td>
                    <td className="px-2 py-1.5 text-right text-cyan-700">{p.appts}</td>
                    <td className="px-2 py-1.5 text-right">{p.days}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <p className="text-[11px] text-slate-500">
        Knocks count inside the area's outline, whoever knocked — the same counting as the canvas day tiles. "Not assigned" means the person
        knocked an area they were never put on. Expired areas keep the numbers from the days they were open.
      </p>
    </div>
  );
}

function Tile({ label, value, hint, tone = "text-slate-900" }: { label: string; value: string; hint?: string; tone?: string }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2.5">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-xl font-extrabold tabular-nums ${tone}`}>{value}</div>
      {hint && <div className="text-[10px] text-slate-500">{hint}</div>}
    </div>
  );
}
